import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { Platform } from "./platform";

const BASE_PATH = process.env.REACT_APP_BASE_PATH ?? "/";

function isInternalUrl(url: string): boolean {
  if (url.startsWith("//")) {
    return false;
  }
  if (url.startsWith("/") || url.startsWith("#")) {
    return true;
  }
  try {
    return new URL(url).origin === globalThis.location.origin;
  } catch {
    return false;
  }
}

/**
 * Prepends the build-time `REACT_APP_BASE_PATH` to a root-relative path so it
 * stays inside the same deployment when handed to `window.open`.
 *
 * In production `BASE_PATH` is `/` and the path is returned as-is. In PR-preview
 * deployments it's `/pr-{N}/` — without this prefix `window.open("/foo")` would
 * land at `https://host/foo` instead of `https://host/pr-{N}/foo` and break SSO.
 *
 * Idempotent: if the path already starts with the base path, returns it unchanged.
 *
 * Exported for testing.
 */
export function withBasePath(path: string): string {
  if (BASE_PATH === "/" || BASE_PATH === "") {
    return path;
  }
  const base = BASE_PATH.endsWith("/") ? BASE_PATH.slice(0, -1) : BASE_PATH;
  if (path === base || path.startsWith(`${base}/`)) {
    return path;
  }
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Returns a function that opens a URL in the appropriate context based on platform and URL type.
 *
 * On native mobile:
 * - Internal URLs (same origin) navigate via React Router to preserve the auth session and SPA state
 * - External URLs open in an in-app browser (SFSafariViewController / Chrome Custom Tabs)
 *
 * On web, opens a new tab. Root-relative internal URLs are prefixed with the
 * build-time base path so PR-preview deployments stay inside their `/pr-{N}/` namespace.
 */
export function useOpenUrl() {
  const history = useHistory();

  return useCallback(
    async (url: string): Promise<void> => {
      if (Platform.isMobile) {
        if (isInternalUrl(url)) {
          const { pathname, search, hash } = new URL(url, globalThis.location.origin);
          history.push(`${pathname}${search}${hash}`);
        } else {
          await openInBrowser({ url });
        }
      } else {
        const target = url.startsWith("/") ? withBasePath(url) : url;
        window.open(target, "_blank", "noopener,noreferrer");
      }
    },
    [history]
  );
}

/**
 * Opens a URL that needs to be fetched asynchronously, without tripping Safari's
 * popup blocker. Safari requires `window.open` to run synchronously inside a
 * user-gesture handler; an awaited fetch before `window.open` breaks that chain
 * and the popup is silently dropped.
 *
 * On web, this opens an `about:blank` placeholder tab synchronously when called,
 * then redirects it to the resolved URL. On Capacitor mobile (where window.open
 * is unsupported and Capacitor's Browser.open is not gesture-restricted), it
 * just delegates to `useOpenUrl`. If the placeholder is blocked outright on web,
 * falls back to a same-tab navigation — opening another popup post-await would
 * hit the same blocker and silently fail.
 *
 * The returned function MUST be invoked synchronously inside the click handler;
 * any `await` before the call will reintroduce the popup-blocker bug.
 */
export function useOpenAsyncUrl() {
  const openUrl = useOpenUrl();

  return useCallback(
    async (getUrl: () => Promise<string>): Promise<void> => {
      const placeholderTab = Platform.isMobile ? null : globalThis.open("about:blank", "_blank");

      try {
        const url = await getUrl();

        if (placeholderTab && !placeholderTab.closed) {
          placeholderTab.opener = null;
          placeholderTab.location.href = url;
        } else if (Platform.isMobile) {
          await openUrl(url);
        } else {
          globalThis.location.assign(url);
        }
      } catch (error) {
        placeholderTab?.close();
        throw error;
      }
    },
    [openUrl]
  );
}
